/**
 * Schema.org JSON-LD components for app.ortex.com.
 *
 * Each helper emits a single <script type="application/ld+json"> tag.
 * Google's Rich Results, Bing, and AI answer engines (ChatGPT,
 * Perplexity, Claude, Gemini) all parse this format — it's the most
 * reliable way to tell crawlers what entity each page is about and
 * how the site is structured, especially for finance content where
 * the same ticker can refer to different securities across exchanges.
 *
 * Sister implementation: ortex.news/src/components/JsonLd.tsx. The
 * patterns there (NewsArticle, CollectionPage, ItemList, paywall
 * disclosure via hasPart) are intentionally mirrored here.
 */
import { Stock } from "@/models/stock";
import { buildStockOgImage } from "@/lib/stock-og";
import { stockCanonicalUrl } from "@/lib/stock-url";

const SITE = "https://app.ortex.com";
const PUBLIC_SITE = "https://public.ortex.com";

/**
 * The one ORTEX Organization entity, shared by all four sites (app.ortex.com,
 * public.ortex.com, ortex.news, ortexcharts.com). public.ortex.com is the
 * corporate site, so it owns the node; everyone else uses the same @id so search
 * engines merge them into one entity instead of four.
 */
const ORGANIZATION_ID = `${PUBLIC_SITE}/#organization`;
const WEBSITE_ID = `${SITE}/#website`;
/** Reference to the shared Organization, for publisher / provider fields. */
const ORGANIZATION_REF = { "@type": "Organization", "@id": ORGANIZATION_ID, name: "ORTEX" };

interface JsonLdProps {
    data: Record<string, unknown> | Array<Record<string, unknown>>;
}

function JsonLd({ data }: JsonLdProps) {
    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
    );
}

/**
 * Organization — emitted once, globally. Tells Google about the
 * company entity so the Knowledge Panel can attach to ORTEX queries
 * and so AI answer engines have a canonical source for "what is
 * ORTEX". Mirrors the canonical node public.ortex.com publishes (same @id);
 * `sameAs` lists third-party profiles only, since our own domains share the @id.
 */
export function OrganizationJsonLd() {
    return (
        <JsonLd
            data={{
                "@context": "https://schema.org",
                "@type": "Organization",
                "@id": ORGANIZATION_ID,
                name: "ORTEX",
                legalName: "ORTEX Technologies Limited",
                url: PUBLIC_SITE,
                logo: {
                    "@type": "ImageObject",
                    url: `${PUBLIC_SITE}/brand/ortex-icon.png`,
                    width: 256,
                    height: 256,
                },
                sameAs: ["https://twitter.com/ORTEX", "https://www.linkedin.com/company/ortex", "https://github.com/ortex-financial"],
            }}
        />
    );
}

/**
 * WebSite — global, emitted once. Declares the canonical site URL
 * and a SearchAction so Google can offer a sitelink-style search box
 * for ORTEX queries. The `urlTemplate` points at the stock-search
 * landing on app.ortex.com; query terms are passed as `q`.
 */
export function WebSiteJsonLd() {
    return (
        <JsonLd
            data={{
                "@context": "https://schema.org",
                "@type": "WebSite",
                "@id": WEBSITE_ID,
                name: "ORTEX",
                alternateName: "ORTEX: Global Financial Analytics",
                url: SITE,
                inLanguage: "en",
                publisher: ORGANIZATION_REF,
                potentialAction: {
                    "@type": "SearchAction",
                    target: {
                        "@type": "EntryPoint",
                        urlTemplate: `${SITE}/?q={search_term_string}`,
                    },
                    "query-input": "required name=search_term_string",
                },
            }}
        />
    );
}

/**
 * Stock page — emits FinancialProduct + WebPage + BreadcrumbList +
 * paywall disclosure as a single @graph. This is the most leverage-y
 * piece of structured data on the whole site: it tells Google and AI
 * engines that a given URL is the canonical hub for one specific
 * financial instrument, identified by ticker AND exchange AND ISIN
 * (since the ticker alone is ambiguous globally).
 *
 * Paywall disclosure: the live data tiles render only for
 * authenticated users; `isAccessibleForFree: true` covers the
 * structural / overview content, and the `hasPart` block tells
 * Google which specific element is paywalled so we get flexible-
 * sampling credit instead of soft-paywall penalty.
 */
export function StockJsonLd({ stock }: { stock: Stock }) {
    const url = stockCanonicalUrl(stock);
    const { ogImageUrl } = buildStockOgImage(stock);
    const sectorPath = Array.isArray(stock.sector) && stock.sector.length
        ? stock.sector.join(" / ")
        : undefined;

    const financialProduct: Record<string, unknown> = {
        "@type": "FinancialProduct",
        "@id": `${url}#instrument`,
        name: stock.name || stock.ticker,
        alternateName: stock.ticker,
        tickerSymbol: stock.ticker,
        category: stockTypeLabel(stock.type),
        url,
        ...(stock.isin ? { identifier: { "@type": "PropertyValue", propertyID: "ISIN", value: stock.isin } } : {}),
        ...(stock.exchangesymbol
            ? { provider: { "@type": "Organization", name: stock.exchangesymbol } }
            : {}),
        ...(stock.exchangeCountry ? { areaServed: stock.exchangeCountry } : {}),
        ...(stock.currency ? { feesAndCommissionsSpecification: stock.currency } : {}),
        ...(sectorPath ? { category: sectorPath } : {}),
    };

    const webPage: Record<string, unknown> = {
        "@type": "WebPage",
        "@id": url,
        url,
        name: stock.name
            ? `${stock.name}, ${stock.exchangesymbol}:${stock.ticker}`
            : `${stock.exchangesymbol}:${stock.ticker}`,
        description: stock.name
            ? `Live short interest, options flow, analyst consensus, insider transactions, and ORTEX scores for ${stock.name} (${stock.exchangesymbol}:${stock.ticker}).`
            : `ORTEX analytics for ${stock.exchangesymbol}:${stock.ticker}.`,
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", "@id": WEBSITE_ID },
        primaryImageOfPage: { "@type": "ImageObject", url: ogImageUrl, width: 1200, height: 630 },
        mainEntity: { "@id": `${url}#instrument` },
        isAccessibleForFree: true,
        // The live tiles (real-time short estimate, options chain,
        // intraday charts) require a subscription. Telling Google which
        // selector is paywalled lets us claim flexible-sampling credit
        // instead of being flagged for soft-paywall cloaking.
        hasPart: {
            "@type": "WebPageElement",
            isAccessibleForFree: false,
            cssSelector: ".live-data-paywalled",
        },
    };

    // No exchange level: there is no /s/<exchange> page, so that crumb was a 404.
    const breadcrumbs = {
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "ORTEX", item: SITE },
            { "@type": "ListItem", position: 2, name: stock.ticker, item: url },
        ],
    };

    return (
        <JsonLd
            data={{
                "@context": "https://schema.org",
                "@graph": [webPage, financialProduct, breadcrumbs],
            }}
        />
    );
}

/**
 * Generic breadcrumb helper for hub pages. Pass an ordered list of
 * `{name, path}` entries; the component builds an absolute-URL
 * BreadcrumbList. The first entry is always "ORTEX" pointing at the
 * homepage, so callers only need to provide the trail beneath it.
 */
export function BreadcrumbJsonLd({ trail }: { trail: { name: string; path: string }[] }) {
    const itemListElement = [
        { "@type": "ListItem", position: 1, name: "ORTEX", item: SITE },
        ...trail.map((entry, i) => ({
            "@type": "ListItem",
            position: i + 2,
            name: entry.name,
            item: `${SITE}${entry.path}`,
        })),
    ];
    return (
        <JsonLd
            data={{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement,
            }}
        />
    );
}

// Maps the numeric `Stock.type` discriminator to a human-readable
// Schema.org category. Unknown types fall back to "Stock" rather than
// emitting an empty field — Google prefers a sensible default over a
// missing one. The list mirrors the type filters used elsewhere in
// the GUI (1=stock, 2=etf, 3=index, 4=fx, 5=commodity, 6=crypto).
function stockTypeLabel(type: number): string {
    switch (type) {
        case 1: return "Stock";
        case 2: return "ETF";
        case 3: return "Index";
        case 4: return "Currency";
        case 5: return "Commodity";
        case 6: return "Cryptocurrency";
        default: return "Stock";
    }
}

/**
 * Data page (short interest, insiders, buybacks ...): a WebPage that is about
 * a Dataset, plus a DefinedTermSet of the metric definitions shown in the page's
 * "About this data" block.
 *
 * The definitions are the part AI answer engines quote ("what is cost to
 * borrow"), so they are emitted as DefinedTerms with the page as their source,
 * and the same words are visible in the server-rendered HTML; structured data
 * that is not on the page is ignored, or worse, by Google.
 *
 * Only the Dataset is marked `isAccessibleForFree: false` (the live figures need
 * a subscription); the WebPage is not, because its text, the definitions, is
 * readable without logging in.
 */
export function MetricPageJsonLd({
    url,
    name,
    description,
    definitions,
    stock,
}: {
    url: string;
    name: string;
    description: string;
    definitions: { term: string; definition: string }[];
    stock?: { name: string; ticker: string; exchangesymbol: string };
}) {
    const datasetId = `${url}#dataset`;
    const termsId = `${url}#definitions`;
    return (
        <JsonLd
            data={{
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "WebPage",
                        "@id": url,
                        url,
                        name,
                        description,
                        inLanguage: "en",
                        isPartOf: { "@type": "WebSite", "@id": WEBSITE_ID },
                        publisher: ORGANIZATION_REF,
                        mainEntity: { "@id": datasetId },
                        hasPart: { "@id": termsId },
                        ...(stock
                            ? {
                                  about: {
                                      "@type": "Corporation",
                                      name: stock.name,
                                      tickerSymbol: `${stock.exchangesymbol}:${stock.ticker}`,
                                  },
                              }
                            : {}),
                    },
                    {
                        "@type": "Dataset",
                        "@id": datasetId,
                        name,
                        description,
                        url,
                        creator: ORGANIZATION_REF,
                        isAccessibleForFree: false,
                        variableMeasured: definitions.map((d) => ({
                            "@type": "PropertyValue",
                            name: d.term,
                            description: d.definition,
                        })),
                    },
                    {
                        "@type": "DefinedTermSet",
                        "@id": termsId,
                        name: `${name}: definitions`,
                        hasDefinedTerm: definitions.map((d) => ({
                            "@type": "DefinedTerm",
                            name: d.term,
                            description: d.definition,
                            inDefinedTermSet: termsId,
                        })),
                    },
                ],
            }}
        />
    );
}
